import React from "react";
import { Loader2 } from "lucide-react";

export function Btn({ children, variant = "primary", size = "md", className = "", ...rest }) {
  const base = "inline-flex items-center gap-1.5 font-medium rounded-lg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";
  const sizes = { sm: "px-2.5 py-1.5 text-xs", md: "px-3.5 py-2 text-sm", lg: "px-5 py-2.5 text-sm" };
  const variantStyles = {
    primary: { style: { background: "var(--primary)", color: "white", border: "1px solid var(--primary)" }, hover: { background: "var(--primary-2)", borderColor: "var(--primary-2)" } },
    secondary: { style: { background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--border)" }, hover: { background: "var(--bg-2)", borderColor: "var(--border-strong)" } },
    ghost: { style: { background: "transparent", color: "var(--muted)", border: "1px solid transparent" }, hover: { background: "var(--bg-2)", color: "var(--ink)" } },
    danger: { style: { background: "var(--danger)", color: "white", border: "1px solid var(--danger)" }, hover: { background: "#B91C1C" } },
    outline_blue: { style: { background: "transparent", color: "var(--primary)", border: "1px solid var(--primary)" }, hover: { background: "var(--primary-soft)" } },
  };
  const v = variantStyles[variant] || variantStyles.primary;
  return (
    <button
      {...rest}
      style={{ ...v.style, ...(rest.style || {}) }}
      className={`${base} ${sizes[size]} ${className}`}
      onMouseEnter={(e) => { Object.assign(e.currentTarget.style, v.hover); rest.onMouseEnter?.(e); }}
      onMouseLeave={(e) => { Object.assign(e.currentTarget.style, v.style); rest.onMouseLeave?.(e); }}
    >
      {children}
    </button>
  );
}

export function Loader() {
  return (
    <div className="flex items-center gap-2 text-sm muted py-10 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--primary)" }} /> Loading...
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", hint, action }) {
  return (
    <div className="surface rounded-xl p-10 text-center" style={{ border: "1px dashed var(--border-strong)" }}>
      <div className="text-base font-semibold ink">{title}</div>
      {hint && <div className="text-sm muted mt-1.5 max-w-md mx-auto">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Field({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium ink mb-1.5">
        {label}{required && <span className="danger ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  border: "1px solid var(--border)",
  borderRadius: "8px",
  background: "var(--surface)",
  fontFamily: "'Geist', sans-serif",
  transition: "border-color 150ms, box-shadow 150ms",
};
const inputCls = "w-full px-3 py-2 text-sm focus:outline-none";

function focusIn(e) { e.currentTarget.style.borderColor = "var(--primary)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(79,70,229,0.1)"; }
function focusOut(e) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "none"; }

export function TextInput({ className = "", style = {}, ...rest }) {
  return <input style={{ ...inputStyle, ...style }} onFocus={(e) => { focusIn(e); rest.onFocus?.(e); }} onBlur={(e) => { focusOut(e); rest.onBlur?.(e); }} className={`${inputCls} ${className}`} {...rest} />;
}
export function TextArea({ className = "", style = {}, ...rest }) {
  return <textarea rows={4} style={{ ...inputStyle, ...style }} onFocus={(e) => { focusIn(e); rest.onFocus?.(e); }} onBlur={(e) => { focusOut(e); rest.onBlur?.(e); }} className={`${inputCls} ${className}`} {...rest} />;
}
export function SelectInput({ children, className = "", style = {}, ...rest }) {
  return (
    <select style={{ ...inputStyle, ...style }} onFocus={(e) => { focusIn(e); rest.onFocus?.(e); }} onBlur={(e) => { focusOut(e); rest.onBlur?.(e); }} className={`${inputCls} ${className}`} {...rest}>
      {children}
    </select>
  );
}
